import { html, css, unsafeCSS, unsafeHTML } from '../shared/vendor.js';
import { SadvancedLitElement } from '../base/sadv-lit-element.js';
import { resolveFileIcon } from './FileTreeIcons.js';

/**
 * SadvFileTree - A comprehensive file tree component with drag-drop, search, CRUD operations
 *
 * @fires node-select - Fired when a node is selected
 * @fires node-expand - Fired when a node is expanded
 * @fires node-collapse - Fired when a node is collapsed
 * @fires node-delete - Fired when a node is deleted
 * @fires node-rename - Fired when a node is renamed
 * @fires node-add - Fired when a new node is added
 * @fires node-move - Fired when a node is moved
 * @fires context-menu - Fired when right-click context menu is triggered
 *
 * @property {Array} data - Tree data structure
 * @property {Boolean} searchable - Enable search functionality
 * @property {Boolean} draggable - Enable drag and drop
 * @property {Boolean} editable - Enable editing (rename, delete, add)
 * @property {Boolean} multiSelect - Enable multiple selection
 * @property {String} theme - Theme: 'light' | 'dark' | 'auto'
 * @property {Boolean} showIcons - Show file/folder icons
 * @property {Boolean} showCheckboxes - Show checkboxes for selection
 * @property {Object} icons - Custom icons configuration
 */
export class RdFileTree extends SadvancedLitElement {
  static properties = {
    data: { type: Array },
    searchable: { type: Boolean },
    draggable: { type: Boolean },
    editable: { type: Boolean },
    multiSelect: { type: Boolean },
    theme: { type: String },
    showIcons: { type: Boolean },
    showCheckboxes: { type: Boolean },
    icons: { type: Object },
    selectedNodes: { type: Array, state: true },
    expandedNodes: { type: Set, state: true },
    searchQuery: { type: String, state: true },
    draggedNode: { type: Object, state: true },
    editingNode: { type: String, state: true },
    contextMenuNode: { type: Object, state: true },
    contextMenuPosition: { type: Object, state: true },
  };

  // No shadow DOM styles needed - using light DOM with external SCSS

  constructor() {
    super();
    this.data = [];
    this.searchable = true;
    this.draggable = true;
    this.editable = true;
    this.multiSelect = false;
    this.theme = 'light';
    this.showIcons = true;
    this.showCheckboxes = false;
    this.selectedNodes = [];
    this.expandedNodes = new Set();
    this.searchQuery = '';
    this.draggedNode = null;
    this.editingNode = null;
    this.contextMenuNode = null;
    this.contextMenuPosition = null;
    this.icons = {
      folder: '📁',
      folderOpen: '📂',
      file: '📄',
      expand: '▶',
      search: '🔍',
      add: '➕',
      delete: '🗑️',
      edit: '✏️',
      move: '↔️',
      newFile: '📄',
      newFolder: '📁',
      expandAll: '⬇',
      collapseAll: '⬆',
    };

    // Bind methods
    this._handleClickOutside = this._handleClickOutside.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this._handleClickOutside);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this._handleClickOutside);
  }

  // ========================================
  // Public API Methods
  // ========================================

  /**
   * Add a new node to the tree
   * @param {Object} node - Node data
   * @param {String} parentId - Parent node ID (null for root)
   */
  addNode(node, parentId = null) {
    const newNode = {
      id: node.id || this._generateId(),
      name: node.name || 'New Item',
      type: node.type || 'file',
      children: node.children || [],
      data: node.data || {},
      ...node,
    };

    if (parentId) {
      const parent = this._findNodeById(this.data, parentId);
      if (parent) {
        if (!parent.children) parent.children = [];
        parent.children.push(newNode);
        this.expandedNodes.add(parentId);
      }
    } else {
      this.data = [...this.data, newNode];
    }

    this.requestUpdate();
    this._dispatchEvent('node-add', { node: newNode, parentId });
    return newNode;
  }

  /**
   * Delete a node from the tree
   * @param {String} nodeId - Node ID to delete
   */
  deleteNode(nodeId) {
    const deleted = this._deleteNodeById(this.data, nodeId);
    if (deleted) {
      this.selectedNodes = this.selectedNodes.filter((id) => id !== nodeId);
      this.expandedNodes.delete(nodeId);
      this.requestUpdate();
      this._dispatchEvent('node-delete', { nodeId, node: deleted });
    }
    return deleted;
  }

  /**
   * Rename a node
   * @param {String} nodeId - Node ID to rename
   * @param {String} newName - New name
   */
  renameNode(nodeId, newName) {
    const node = this._findNodeById(this.data, nodeId);
    if (node) {
      const oldName = node.name;
      node.name = newName;
      this.requestUpdate();
      this._dispatchEvent('node-rename', { nodeId, oldName, newName, node });
    }
    return node;
  }

  /**
   * Move a node to a new parent
   * @param {String} nodeId - Node ID to move
   * @param {String} newParentId - New parent ID (null for root)
   */
  moveNode(nodeId, newParentId) {
    const node = this._findNodeById(this.data, nodeId);
    if (!node) return false;

    // Remove from old location
    const deleted = this._deleteNodeById(this.data, nodeId);
    if (!deleted) return false;

    // Add to new location
    if (newParentId) {
      const newParent = this._findNodeById(this.data, newParentId);
      if (newParent && newParent.type === 'folder') {
        if (!newParent.children) newParent.children = [];
        newParent.children.push(node);
        this.expandedNodes.add(newParentId);
      } else {
        return false;
      }
    } else {
      this.data = [...this.data, node];
    }

    this.requestUpdate();
    this._dispatchEvent('node-move', { nodeId, newParentId, node });
    return true;
  }

  /**
   * Select a node
   * @param {String} nodeId - Node ID to select
   * @param {Boolean} multi - Multi-select mode
   */
  selectNode(nodeId, multi = false) {
    if (multi && this.multiSelect) {
      if (this.selectedNodes.includes(nodeId)) {
        this.selectedNodes = this.selectedNodes.filter((id) => id !== nodeId);
      } else {
        this.selectedNodes = [...this.selectedNodes, nodeId];
      }
    } else {
      this.selectedNodes = [nodeId];
    }
    this.requestUpdate();
  }

  /**
   * Get selected nodes
   * @returns {Array} Selected nodes data
   */
  getSelectedNodes() {
    return this.selectedNodes.map((id) => this._findNodeById(this.data, id)).filter(Boolean);
  }

  /**
   * Expand a node
   * @param {String} nodeId - Node ID to expand
   */
  expandNode(nodeId) {
    this.expandedNodes.add(nodeId);
    this.requestUpdate();
    this._dispatchEvent('node-expand', { nodeId });
  }

  /**
   * Collapse a node
   * @param {String} nodeId - Node ID to collapse
   */
  collapseNode(nodeId) {
    this.expandedNodes.delete(nodeId);
    this.requestUpdate();
    this._dispatchEvent('node-collapse', { nodeId });
  }

  /**
   * Expand all nodes
   */
  expandAll() {
    this._traverseTree(this.data, (node) => {
      if (node.type === 'folder' && node.children && node.children.length > 0) {
        this.expandedNodes.add(node.id);
      }
    });
    this.requestUpdate();
  }

  /**
   * Collapse all nodes
   */
  collapseAll() {
    this.expandedNodes.clear();
    this.requestUpdate();
  }

  /**
   * Set tree data
   * @param {Array} data - Tree data
   */
  setData(data) {
    this.data = data;
    this.requestUpdate();
  }

  /**
   * Get tree data
   * @returns {Array} Tree data
   */
  getData() {
    return this.data;
  }

  // ========================================
  // Private Helper Methods
  // ========================================

  _icon(key) {
    const v = this.icons[key];
    return typeof v === 'string' ? unsafeHTML(v) : v;
  }

  _resolveFileIcon(filename) {
    const resolved = resolveFileIcon(filename);
    return resolved ? unsafeHTML(resolved) : this._icon('file');
  }

  _isDescendant(ancestor, node) {
    if (ancestor.id === node.id) return true;
    if (ancestor.children) return ancestor.children.some((c) => this._isDescendant(c, node));
    return false;
  }

  _findParentNode(nodes, childId, parent = null) {
    for (const node of nodes) {
      if (node.id === childId) return parent;
      if (node.children) {
        const found = this._findParentNode(node.children, childId, node);
        if (found !== undefined) return found;
      }
    }
    return undefined;
  }

  _getDragTargetFolder(node) {
    if (node.type === 'folder') return node;
    const parent = this._findParentNode(this.data, node.id);
    return parent ?? null;
  }

  _getSelectedFolderId() {
    if (this.selectedNodes.length === 0) return null;
    const node = this._findNodeById(this.data, this.selectedNodes[0]);
    if (!node) return null;
    if (node.type === 'folder') return node.id;
    return this._findParentNode(this.data, node.id)?.id ?? null;
  }

  _generateId() {
    return `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  _findNodeById(nodes, id) {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children) {
        const found = this._findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  }

  _deleteNodeById(nodes, id) {
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].id === id) {
        return nodes.splice(i, 1)[0];
      }
      if (nodes[i].children) {
        const deleted = this._deleteNodeById(nodes[i].children, id);
        if (deleted) return deleted;
      }
    }
    return null;
  }

  _traverseTree(nodes, callback) {
    nodes.forEach((node) => {
      callback(node);
      if (node.children) {
        this._traverseTree(node.children, callback);
      }
    });
  }

  _filterNodes(nodes, query) {
    if (!query) return nodes;

    const filtered = [];
    const lowerQuery = query.toLowerCase();

    nodes.forEach((node) => {
      const matches = node.name.toLowerCase().includes(lowerQuery);
      const filteredChildren = node.children ? this._filterNodes(node.children, query) : [];

      if (matches || filteredChildren.length > 0) {
        filtered.push({
          ...node,
          children: filteredChildren,
          _highlight: matches,
        });
        if (filteredChildren.length > 0) {
          this.expandedNodes.add(node.id);
        }
      }
    });

    return filtered;
  }

  _highlightText(text, query) {
    if (!query) return text;

    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part) => (part.toLowerCase() === query.toLowerCase() ? html`<span class="highlight">${part}</span>` : part));
  }

  _dispatchEvent(name, detail) {
    this.dispatchEvent(
      new CustomEvent(name, {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  _getCheckboxState(node) {
    if (!node.children || node.children.length === 0) {
      return this.selectedNodes.includes(node.id) ? 'checked' : 'unchecked';
    }

    const selectedChildren = node.children.filter((child) => {
      const state = this._getCheckboxState(child);
      return state === 'checked' || state === 'indeterminate';
    });

    if (selectedChildren.length === 0) return 'unchecked';
    if (selectedChildren.length === node.children.length) return 'checked';
    return 'indeterminate';
  }

  // ========================================
  // Event Handlers
  // ========================================

  _handleNodeClick(node, event) {
    event.stopPropagation();

    if (this.showCheckboxes) {
      this._toggleNodeSelection(node, event.ctrlKey || event.metaKey);
    } else {
      this.selectNode(node.id, event.ctrlKey || event.metaKey);
      this._dispatchEvent('node-select', { node, selected: true });
    }
  }

  _handleNodeDoubleClick(node, event) {
    event.stopPropagation();
    if (node.type === 'folder') {
      this._toggleExpand(node);
    }
    this._dispatchEvent('node-dblclick', { node });
  }

  _toggleExpand(node) {
    if (this.expandedNodes.has(node.id)) {
      this.collapseNode(node.id);
    } else {
      this.expandNode(node.id);
    }
  }

  _toggleNodeSelection(node, multi = false) {
    const state = this._getCheckboxState(node);
    const newState = state === 'checked' ? false : true;

    // Update this node and all children
    const updateSelection = (n, selected) => {
      if (selected) {
        if (!this.selectedNodes.includes(n.id)) {
          this.selectedNodes = [...this.selectedNodes, n.id];
        }
      } else {
        this.selectedNodes = this.selectedNodes.filter((id) => id !== n.id);
      }

      if (n.children) {
        n.children.forEach((child) => updateSelection(child, selected));
      }
    };

    updateSelection(node, newState);
    this.requestUpdate();
  }

  _handleExpandClick(node, event) {
    event.stopPropagation();
    this._toggleExpand(node);
  }

  _handleContextMenu(node, event) {
    event.preventDefault();
    event.stopPropagation();

    this.contextMenuNode = node;
    this.contextMenuPosition = {
      x: event.clientX,
      y: event.clientY,
    };
    this.requestUpdate();

    this._dispatchEvent('context-menu', { node, position: this.contextMenuPosition });
  }

  _handleClickOutside(event) {
    if (this.contextMenuPosition && !event.target.closest('.context-menu')) {
      this.contextMenuPosition = null;
      this.requestUpdate();
    }
  }

  _handleSearch(event) {
    this.searchQuery = event.target.value;
    this.requestUpdate();
  }

  _clearSearch() {
    this.searchQuery = '';
    this.requestUpdate();
  }

  _startEditing(node, event) {
    event.stopPropagation();
    this.editingNode = node.id;
    this.requestUpdate();

    // Focus input after render (light DOM - no shadowRoot)
    setTimeout(() => {
      const input = this.querySelector(`input[data-node-id="${node.id}"]`);
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  _handleEditKeyDown(node, event) {
    if (event.key === 'Enter') {
      this._saveEdit(node, event.target.value);
    } else if (event.key === 'Escape') {
      this.editingNode = null;
      this.requestUpdate();
    }
  }

  _handleEditBlur(node, event) {
    this._saveEdit(node, event.target.value);
  }

  _saveEdit(node, newName) {
    if (newName && newName.trim() && newName !== node.name) {
      this.renameNode(node.id, newName.trim());
    }
    this.editingNode = null;
    this.requestUpdate();
  }

  // Drag and Drop Handlers
  _handleDragStart(node, event) {
    if (!this.draggable) return;

    this.draggedNode = node;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', node.id);

    // Add dragging class
    setTimeout(() => {
      event.target.classList.add('dragging');
    }, 0);
  }

  _handleDragEnd(event) {
    event.target.classList.remove('dragging');
    this.querySelectorAll('.node-content.drag-over').forEach((el) => el.classList.remove('drag-over'));
    this.draggedNode = null;
    this.requestUpdate();
  }

  _handleDragOver(node, event) {
    if (!this.draggable || !this.draggedNode) return;
    const folder = this._getDragTargetFolder(node);
    if (!folder || folder.id === this.draggedNode.id) return;
    if (this._isDescendant(this.draggedNode, folder)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }

  _handleDragEnter(node, event) {
    if (!this.draggable || !this.draggedNode) return;
    const folder = this._getDragTargetFolder(node);
    if (!folder || folder.id === this.draggedNode.id) return;
    if (this._isDescendant(this.draggedNode, folder)) return;
    this.querySelectorAll('.node-content.drag-over').forEach((el) => el.classList.remove('drag-over'));
    const folderEl = this.querySelector(`.node-content[data-node-id="${folder.id}"]`);
    if (folderEl) folderEl.classList.add('drag-over');
  }

  _handleDragLeave(event) {
    if (!this.contains(event.relatedTarget)) {
      this.querySelectorAll('.node-content.drag-over').forEach((el) => el.classList.remove('drag-over'));
    }
  }

  _handleDrop(node, event) {
    event.preventDefault();
    event.stopPropagation();
    this.querySelectorAll('.node-content.drag-over').forEach((el) => el.classList.remove('drag-over'));
    if (!this.draggable || !this.draggedNode) return;
    const folder = this._getDragTargetFolder(node);
    if (!folder || folder.id === this.draggedNode.id) return;
    if (this._isDescendant(this.draggedNode, folder)) return;
    this.moveNode(this.draggedNode.id, folder.id);
    this.draggedNode = null;
  }

  async _promptAddNode(type, parentId) {
    const result = await window.AlertManager?.prompt(
      '',
      [{ name: 'name', type: 'text', label: `${type === 'folder' ? 'Folder' : 'File'} name`, value: type === 'folder' ? 'New Folder' : 'New File' }],
      `New ${type === 'folder' ? 'Folder' : 'File'}`,
    );
    if (result?.action === 'submit' && result.values?.name?.trim()) {
      this.addNode({ name: result.values.name.trim(), type }, parentId);
    }
  }

  async _confirmDeleteNode(node) {
    const result = await window.AlertManager?.confirm(`Delete "${node.name}"?`, 'Delete', 'danger');
    if (result?.action === 'confirm') this.deleteNode(node.id);
  }

  // Context Menu Actions
  _contextMenuAction(action, node) {
    this.contextMenuPosition = null;

    switch (action) {
      case 'add-file':
        this._promptAddNode('file', node.id);
        break;
      case 'add-folder':
        this._promptAddNode('folder', node.id);
        break;
      case 'rename':
        this._startEditing(node, { stopPropagation: () => {} });
        break;
      case 'delete':
        this._confirmDeleteNode(node);
        break;
      case 'expand-all':
        this.expandAll();
        break;
      case 'collapse-all':
        this.collapseAll();
        break;
    }

    this.requestUpdate();
  }

  // ========================================
  // Render Methods
  // ========================================

  _renderNode(node, level = 0) {
    const isExpanded = this.expandedNodes.has(node.id);
    const isSelected = this.selectedNodes.includes(node.id);
    const isEditing = this.editingNode === node.id;
    const hasChildren = node.children && node.children.length > 0;
    const checkboxState = this.showCheckboxes ? this._getCheckboxState(node) : null;

    return html`
      <div class="tree-node">
        <div
          class="node-content ${isSelected ? 'selected' : ''}"
          data-node-id="${node.id}"
          @click=${(e) => this._handleNodeClick(node, e)}
          @dblclick=${(e) => this._handleNodeDoubleClick(node, e)}
          @contextmenu=${(e) => this._handleContextMenu(node, e)}
          draggable=${this.draggable}
          @dragstart=${(e) => this._handleDragStart(node, e)}
          @dragend=${(e) => this._handleDragEnd(e)}
          @dragover=${(e) => this._handleDragOver(node, e)}
          @dragenter=${(e) => this._handleDragEnter(node, e)}
          @dragleave=${(e) => this._handleDragLeave(e)}
          @drop=${(e) => this._handleDrop(node, e)}
        >
          <span class="node-indent" style="width: calc(${level} * var(--tree-indent, 12px))"></span>

          ${hasChildren ? html` <span class="expand-icon ${isExpanded ? 'expanded' : ''}" @click=${(e) => this._handleExpandClick(node, e)}>${this._icon('expand')}</span> ` : html` <span class="expand-icon placeholder">${this._icon('expand')}</span> `}
          ${this.showCheckboxes
            ? html`
                <div
                  class="node-checkbox ${checkboxState === 'checked' ? 'checked' : ''} ${checkboxState === 'indeterminate' ? 'indeterminate' : ''}"
                  @click=${(e) => {
                    e.stopPropagation();
                    this._toggleNodeSelection(node);
                  }}
                ></div>
              `
            : ''}
          ${this.showIcons ? html` <span class="node-icon ${node.type}">${node.type === 'folder' ? (isExpanded ? this._icon('folderOpen') : this._icon('folder')) : this._resolveFileIcon(node.name)}</span> ` : ''}
          ${isEditing
            ? html` <input type="text" class="node-input" data-node-id="${node.id}" .value=${node.name} @keydown=${(e) => this._handleEditKeyDown(node, e)} @blur=${(e) => this._handleEditBlur(node, e)} @click=${(e) => e.stopPropagation()} /> `
            : html` <span class="node-label"> ${node._highlight ? this._highlightText(node.name, this.searchQuery) : node.name} </span> `}
          ${this.editable && !isEditing
            ? html`
                <div class="node-actions">
                  ${node.type === 'folder'
                    ? html`
                        <button
                          class="node-action-btn"
                          title="Add file"
                          @click=${(e) => {
                            e.stopPropagation();
                            this._promptAddNode('file', node.id);
                          }}
                        >
                          ${this._icon('add')}
                        </button>
                      `
                    : ''}
                  <button class="node-action-btn" title="Rename" @click=${(e) => this._startEditing(node, e)}>${this._icon('edit')}</button>
                  <button
                    class="node-action-btn"
                    title="Delete"
                    @click=${(e) => {
                      e.stopPropagation();
                      this._confirmDeleteNode(node);
                    }}
                  >
                    ${this._icon('delete')}
                  </button>
                </div>
              `
            : ''}
        </div>

        ${hasChildren && isExpanded ? html` <div class="node-children">${node.children.map((child) => this._renderNode(child, level + 1))}</div> ` : ''}
      </div>
    `;
  }

  _renderContextMenu() {
    if (!this.contextMenuPosition || !this.contextMenuNode) return '';

    const node = this.contextMenuNode;

    return html`
      <div class="context-menu" style="left: ${this.contextMenuPosition.x}px; top: ${this.contextMenuPosition.y}px" @click=${(e) => e.stopPropagation()}>
        ${node.type === 'folder'
          ? html`
              <div class="context-menu-item" @click=${() => this._contextMenuAction('add-file', node)}>${this._icon('file')} Add File</div>
              <div class="context-menu-item" @click=${() => this._contextMenuAction('add-folder', node)}>${this._icon('folder')} Add Folder</div>
              <div class="context-menu-divider"></div>
            `
          : ''}

        <div class="context-menu-item" @click=${() => this._contextMenuAction('rename', node)}>${this._icon('edit')} Rename</div>
        <div class="context-menu-item danger" @click=${() => this._contextMenuAction('delete', node)}>${this._icon('delete')} Delete</div>

        ${node.type === 'folder'
          ? html`
              <div class="context-menu-divider"></div>
              <div class="context-menu-item" @click=${() => this._contextMenuAction('expand-all', node)}>Expand All</div>
              <div class="context-menu-item" @click=${() => this._contextMenuAction('collapse-all', node)}>Collapse All</div>
            `
          : ''}
      </div>
    `;
  }

  render() {
    const filteredData = this.searchQuery ? this._filterNodes(this.data, this.searchQuery) : this.data;

    return html`
      <div class="file-tree-container">
        ${this.searchable
          ? html`
              <div class="file-tree-header">
                <div class="search-container">
                  <input type="text" class="search-input" placeholder="Search files..." .value=${this.searchQuery} @input=${this._handleSearch} />
                  ${this.searchQuery ? html` <button class="clear-search" @click=${this._clearSearch}>✕</button> ` : html` <span class="search-icon">${this._icon('search')}</span> `}
                </div>

                ${this.editable
                  ? html`
                      <div class="toolbar">
                        <button class="toolbar-btn" title="New File" @click=${() => this._promptAddNode('file', this._getSelectedFolderId())}>${this._icon('newFile')}</button>
                        <button class="toolbar-btn" title="New Folder" @click=${() => this._promptAddNode('folder', this._getSelectedFolderId())}>${this._icon('newFolder')}</button>
                        <span style="flex:1"></span>
                        <button class="toolbar-btn" title="Expand All" @click=${() => this.expandAll()}>${this._icon('expandAll')}</button>
                        <button class="toolbar-btn" title="Collapse All" @click=${() => this.collapseAll()}>${this._icon('collapseAll')}</button>
                      </div>
                    `
                  : ''}
              </div>
            `
          : ''}

        <div class="tree-content">
          ${filteredData.length > 0
            ? html` ${filteredData.map((node) => this._renderNode(node))} `
            : html`
                <div class="empty-state">
                  <div class="empty-state-icon">${this._icon('folder')}</div>
                  <div>No files or folders</div>
                </div>
              `}
        </div>
      </div>

      ${this._renderContextMenu()}
    `;
  }
}
if (!customElements.get('rd-file-tree')) customElements.define('rd-file-tree', RdFileTree);
